import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useInView } from "react-intersection-observer";

import useItemService from "../../services/api/itemService";
import { enums } from "../../constants/enum";
import {
  AddIcon,
  ArrowLongRightIcon,
  CopyIcon,
  LibraryAddIcon,
  PlusIcon,
  RefreshIcon,
} from "../../assets/icons";
import itemImage from "../../assets/images/inventory-item.png";

import {
  Badge,
  Button,
  IconButton,
  ItemFilters,
  ListBox,
  Loading,
  PageTitle,
  Section,
} from "../../components";
import "./InventoryPage.scss";

function InventoryPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = React.useState({
    search: "",
    sph: "",
    cyl: "",
    add: "",
    lenseDiagonal: "",
    bridge: "",
    armLength: "",
    accessoryType: "",
    material: "",
    color: "",
    gender: "",
    modelNumber: "",
    sunglassFrameColor: "",
    sunglassLenseColor: "",
    lenseType: "",
    minPrice: "",
    maxPrice: "",
    sort: enums.ITEM_SORTING_OPTIONS.NEWEST_FIRST,
    stockStatus: enums.ITEM_FILTER_STOCK_STATUS.ALL,
    category: enums.ITEM_FILTER_CATEGORIES.ALL,
  });
  const { ref, inView } = useInView();
  const { getAllItems, queryKey } = useItemService();

  const { data, isLoading, error, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [queryKey, filters],
      queryFn: ({ pageParam = 1 }) =>
        getAllItems({ ...filters, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
      retry: 1,
    });

  function makeBadgeVariant(itemCategory) {
    switch (itemCategory) {
      case enums.ITEM_CATEGORIES.FRAME:
        return "info";
      case enums.ITEM_CATEGORIES.LENSE:
        return "success";
      case enums.ITEM_CATEGORIES.ACCESSORIES:
        return "warning";
      case enums.ITEM_CATEGORIES.SUNGLASS:
        return "danger";
      default:
        return "info";
    }
  }

  function handleCopy(itemId) {
    if (!itemId) return;
    navigator.clipboard.writeText(itemId);
    toast.success("Item ID copied to clipboard");
  }

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  if (isLoading) return <Loading variant="fullScreen" />;
  if (error) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
  }

  return (
    <div className="page inventory-page">
      <PageTitle text="Inventory">
        <div className="action-list-wide">
          <Link to="/inventory/stock/move">
            <Button text="Move Stock" icon={<RefreshIcon />} />
          </Link>
          <Link to="/inventory/add">
            <Button text="Add New Item" icon={<AddIcon />} />
          </Link>
          <Link to="/inventory/stock/add">
            <Button text="Add New Stock" icon={<LibraryAddIcon />} />
          </Link>
        </div>

        <ListBox
          className="action-list"
          options={[
            { text: "Move Stock", icon: <RefreshIcon /> , onClick: () => navigate("/inventory/stock/move") },
            { text: "Add New Item", icon: <PlusIcon /> , onClick: () => navigate("/inventory/add") },
            { text: "Add New Stock", icon: <LibraryAddIcon /> , onClick: () => navigate("/inventory/stock/add") },
          ]}
        />
      </PageTitle>

      <Section>
        <ItemFilters
          initialValues={filters}
          onSubmit={(values) => {
            setFilters(values);
          }}
          onClear={() => {
            setFilters({
              search: "",
              sph: "",
              cyl: "",
              add: "",
              lenseDiagonal: "",
              bridge: "",
              arm_length: "",
              accessoryType: "",
              material: "",
              color: "",
              gender: "",
              modelNumber: "",
              sunglassFrameColor: "",
              sunglassLenseColor: "",
              lenseType: "",
              minPrice: "",
              maxPrice: "",
              sort: enums.ITEM_SORTING_OPTIONS.NEWEST_FIRST,
              stockStatus: enums.ITEM_FILTER_STOCK_STATUS.ALL,
              category: enums.ITEM_FILTER_CATEGORIES.ALL,
            });
          }}
        />

        <div className="inventory-list">
          {data?.pages.map(({ items }) =>
            items.map((item) => (
              <div className="item-card" key={item?._id}>
                <div className="item-img">
                  <div className="img-bx">
                    <img src={itemImage} alt="Item" />
                  </div>
                </div>

                <div className="item-meta">
                  <div className="item-name">
                    <i className="item-brand-letter">T</i>
                    <p>{item?.itemName}</p>
                  </div>

                  <div className="item-stats">
                    <div>
                      <Badge
                        className="badge"
                        text={item?.category}
                        variant={makeBadgeVariant(item?.category)}
                        outlined
                      />
                      <div className="separator"></div>
                      <Badge
                        className="badge"
                        text={item?.inventory?.stockStatus}
                      />
                    </div>

                    <div className="separator"></div>
                    {item?.inventory?.stockStatus ===
                    enums.STOCK_STATUS.READY ? (
                      <div>
                        <p>
                          Storeroom Stock: {item?.inventory?.storeRoomStock}
                        </p>
                        <div className="separator"></div>
                        <p>Showcase Stock: {item?.inventory?.displayStock}</p>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="item-actions">
                  <IconButton
                    icon={<CopyIcon />}
                    onClick={() => handleCopy(item?._id)}
                  />
                  <Link to={`/inventory/details/${item?._id}`}>
                    <Button text="View" prefixIcon={<ArrowLongRightIcon />} />
                  </Link>
                </div>
              </div>
            )),
          )}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            ref={ref}
          >
            <Loading isLoading={isFetchingNextPage} variant="inline" />
          </div>
        </div>
      </Section>
    </div>
  );
}

export default InventoryPage;
